import { type ComponentType, useEffect, useState } from "react";

type DevToolModules = {
  DevPanel: ComponentType;
  BuilderInspectorOverlay: ComponentType;
  PersonalizationDevtoolsOverlay: ComponentType;
};

/**
 * Post-hydration loader for the dev-only tools (DevPanel + Builder inspector
 * overlay). Both are runtime-gated on `devConfig`, but a static import ships
 * their modules (panel + utm tracker + inspector + dev-panel utils, ~25KB) in
 * the root chunk of every PRODUCTION document. Root mounts this only when
 * `devConfig` is set, so on production the dynamic imports never even fire.
 *
 * Renders nothing during SSR and on the first client render in every
 * environment (the effect hasn't run), so server and client markup always
 * agree — the trade is dev-only: the panel button appears a beat after
 * hydration instead of in the initial HTML.
 */
export function DevTools() {
  const [mods, setMods] = useState<DevToolModules | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      import("~/components/dev-panel"),
      import("~/hec/components/builder-inspector/builder-inspector-overlay"),
      // Personalization Tracker + campaign-manifest overlays. This wrapper
      // module is light (the section bodies are a second-level lazy chunk
      // gated behind the Dev Panel toggle), but it still belongs out of the
      // root chunk with the rest of the dev tools (COM-188).
      import("~/components/personalization-devtools-overlay"),
    ]).then(([panel, inspector, pzOverlay]) => {
      if (cancelled) return;
      setMods({
        DevPanel: panel.DevPanel,
        BuilderInspectorOverlay: inspector.BuilderInspectorOverlay,
        PersonalizationDevtoolsOverlay:
          pzOverlay.PersonalizationDevtoolsOverlay,
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!mods) return null;
  return (
    <>
      <mods.DevPanel />
      <mods.BuilderInspectorOverlay />
      <mods.PersonalizationDevtoolsOverlay />
    </>
  );
}
